import React, { useState } from 'react';
import '../styles/AdvancedFilterBar.css';

export interface FilterOptions {
  sentiment: string;
  rating: string;
  source: string;
  searchTerm: string;
  dateRange: string;
  customStartDate?: string;
  customEndDate?: string;
  hasHighlights: string;
  highlightCategory: string;  // Universal category filter
  highlightAngle: string;  // Specific angle filter
  responseStatus: string;
  priority: string;
  productCategory?: string;
  minQuality?: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

interface AdvancedFilterBarProps {
  filters: FilterOptions;
  onFilterChange: (filters: FilterOptions) => void;
  sources: string[];
  products?: string[];
  onExport?: () => void;
  totalCount: number;
  filteredCount: number;
}

const AdvancedFilterBar: React.FC<AdvancedFilterBarProps> = ({
  filters,
  onFilterChange,
  sources,
  products = [],
  onExport,
  totalCount,
  filteredCount
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleChange = (key: keyof FilterOptions, value: any) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const clearFilters = () => {
    onFilterChange({
      sentiment: 'all',
      rating: 'all',
      source: 'all',
      searchTerm: '',
      dateRange: 'all',
      hasHighlights: 'all',
      highlightCategory: 'all',
      highlightAngle: 'all',
      responseStatus: 'all',
      priority: 'all',
      sortBy: 'date',
      sortOrder: 'desc'
    });
  };

  const getDateRangeValues = () => {
    const now = new Date();
    const ranges: { [key: string]: { start: Date; end: Date } } = {
      today: {
        start: new Date(now.setHours(0, 0, 0, 0)),
        end: new Date()
      },
      yesterday: {
        start: new Date(now.setDate(now.getDate() - 1)),
        end: new Date(now.setDate(now.getDate() + 1))
      },
      last7days: {
        start: new Date(now.setDate(now.getDate() - 7)),
        end: new Date()
      },
      last30days: {
        start: new Date(now.setDate(now.getDate() - 30)),
        end: new Date()
      },
      last90days: {
        start: new Date(now.setDate(now.getDate() - 90)),
        end: new Date()
      }
    };
    return ranges[filters.dateRange] || null;
  };

  const activeFilterCount = Object.entries(filters).filter(
    ([key, value]) => value !== 'all' && value !== '' && key !== 'sortBy' && key !== 'sortOrder'
  ).length;

  return (
    <div className="advanced-filter-bar">
      <div className="filter-header">
        <div className="filter-summary">
          <span className="filter-count">
            Showing <strong>{filteredCount}</strong> of <strong>{totalCount}</strong> reviews
          </span>
          {activeFilterCount > 0 && (
            <span className="active-filters">
              {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active
            </span>
          )}
        </div>
        <div className="filter-actions">
          <button 
            className="btn-text"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            {showAdvanced ? 'Hide' : 'Show'} Advanced Filters
          </button>
          {activeFilterCount > 0 && (
            <button className="btn-text" onClick={clearFilters}>
              Clear All
            </button>
          )}
          {onExport && (
            <button className="btn-export" onClick={onExport}>
              Export CSV
            </button>
          )}
        </div>
      </div>

      <div className="filter-main">
        <div className="filter-row">
          {/* Search */}
          <div className="filter-group filter-search">
            <input
              type="text"
              placeholder="Search reviews..."
              value={filters.searchTerm}
              onChange={(e) => handleChange('searchTerm', e.target.value)}
              className="search-input"
            />
          </div>

          {/* Quick Highlights Toggle */}
          <div className="filter-group">
            <label>Highlights</label>
            <select
              value={filters.hasHighlights}
              onChange={(e) => handleChange('hasHighlights', e.target.value)}
              className="filter-select"
            >
              <option value="all">All Reviews</option>
              <option value="yes">With Highlights ✨</option>
              <option value="quality">High Quality Only (4+)</option>
              <option value="no">No Highlights</option>
            </select>
          </div>

          {/* Date Range */}
          <div className="filter-group">
            <label>Date Range</label>
            <select
              value={filters.dateRange}
              onChange={(e) => handleChange('dateRange', e.target.value)}
              className="filter-select"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="last7days">Last 7 Days</option>
              <option value="last30days">Last 30 Days</option>
              <option value="last90days">Last 90 Days</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* Rating */}
          <div className="filter-group">
            <label>Rating</label>
            <select
              value={filters.rating}
              onChange={(e) => handleChange('rating', e.target.value)}
              className="filter-select"
            >
              <option value="all">All Ratings</option>
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
              <option value="3">3 Stars</option>
              <option value="2">2 Stars</option>
              <option value="1">1 Star</option>
              <option value="4+">4+ Stars</option>
              <option value="3-">3 Stars or Less</option>
            </select>
          </div>

          {/* Sentiment */}
          <div className="filter-group">
            <label>Sentiment</label>
            <select
              value={filters.sentiment}
              onChange={(e) => handleChange('sentiment', e.target.value)}
              className="filter-select"
            >
              <option value="all">All Sentiments</option>
              <option value="positive">Positive</option>
              <option value="negative">Negative</option>
              <option value="neutral">Neutral</option>
              <option value="mixed">Mixed</option>
            </select>
          </div>

          {/* Source */}
          <div className="filter-group">
            <label>Source</label>
            <select
              value={filters.source}
              onChange={(e) => handleChange('source', e.target.value)}
              className="filter-select"
            >
              <option value="all">All Sources</option>
              {sources.map(source => (
                <option key={source} value={source}>{source}</option>
              ))}
            </select>
          </div>

          {/* Sort */}
          <div className="filter-group">
            <label>Sort By</label>
            <div className="sort-controls">
              <select
                value={filters.sortBy}
                onChange={(e) => handleChange('sortBy', e.target.value)}
                className="filter-select"
              >
                <option value="date">Date</option>
                <option value="rating">Rating</option>
                <option value="sentiment">Sentiment</option>
                <option value="highlights">Highlights Count</option>
                <option value="quality">Quality Score</option>
              </select>
              <button
                className={`sort-order ${filters.sortOrder}`}
                onClick={() => handleChange('sortOrder', filters.sortOrder === 'asc' ? 'desc' : 'asc')}
                title={filters.sortOrder === 'asc' ? 'Ascending' : 'Descending'}
              >
                {filters.sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </div>

        {/* Custom Date Range */}
        {filters.dateRange === 'custom' && (
          <div className="filter-row custom-date-row">
            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="date"
                value={filters.customStartDate || ''}
                onChange={(e) => handleChange('customStartDate', e.target.value)}
                className="date-input"
              />
            </div>
            <div className="filter-group">
              <label>End Date</label>
              <input
                type="date"
                value={filters.customEndDate || ''}
                onChange={(e) => handleChange('customEndDate', e.target.value)}
                className="date-input"
              />
            </div>
          </div>
        )}

        {/* Advanced Filters */}
        {showAdvanced && (
          <div className="filter-advanced">
            <div className="filter-row">
              {/* Highlight Category (Universal) */}
              <div className="filter-group">
                <label>Highlight Category</label>
                <select
                  value={filters.highlightCategory}
                  onChange={(e) => handleChange('highlightCategory', e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Categories</option>
                  <option value="BENEFIT">Benefit</option>
                  <option value="PROBLEM_SOLVED">Problem Solved</option>
                  <option value="SOCIAL_PROOF">Social Proof</option>
                  <option value="EMOTION">Emotion</option>
                  <option value="UNIQUE_FEATURE">Unique Feature</option>
                </select>
              </div>

              {/* Highlight Angle (Specific) */}
              <div className="filter-group">
                <label>Specific Type</label>
                <select
                  value={filters.highlightAngle}
                  onChange={(e) => handleChange('highlightAngle', e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Types</option>
                  {/* These will be dynamically populated based on brand */}
                  <option value="testosterone_boost">Testosterone Boost</option>
                  <option value="energy">Energy</option>
                  <option value="transformation">Transformation</option>
                  <option value="warmth">Warmth</option>
                  <option value="durability">Durability</option>
                  <option value="moisture_control">Moisture Control</option>
                </select>
              </div>

              {/* Response Status */}
              <div className="filter-group">
                <label>Response Status</label>
                <select
                  value={filters.responseStatus}
                  onChange={(e) => handleChange('responseStatus', e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All</option>
                  <option value="pending">Needs Response</option>
                  <option value="responded">Responded</option>
                  <option value="ignored">Ignored</option>
                </select>
              </div>

              {/* Priority */}
              <div className="filter-group">
                <label>Priority</label>
                <select
                  value={filters.priority}
                  onChange={(e) => handleChange('priority', e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Priorities</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              {/* Product Category */}
              {products.length > 0 && (
                <div className="filter-group">
                  <label>Product</label>
                  <select
                    value={filters.productCategory || 'all'}
                    onChange={(e) => handleChange('productCategory', e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Products</option>
                    {products.map(product => (
                      <option key={product} value={product}>{product}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Min Quality Score */}
              <div className="filter-group">
                <label>Min Quality</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  step="0.5"
                  placeholder="Min quality"
                  value={filters.minQuality || ''}
                  onChange={(e) => handleChange('minQuality', parseFloat(e.target.value) || undefined)}
                  className="number-input"
                />
              </div>
            </div>

            {/* Quick Filters */}
            <div className="quick-filters">
              <span className="quick-filter-label">Quick Filters:</span>
              <button
                className="quick-filter-btn"
                onClick={() => onFilterChange({
                  ...filters,
                  rating: '1',
                  sentiment: 'negative',
                  priority: 'high'
                })}
              >
                Critical Reviews
              </button>
              <button
                className="quick-filter-btn"
                onClick={() => onFilterChange({
                  ...filters,
                  rating: '5',
                  sentiment: 'positive',
                  hasHighlights: 'quality'
                })}
              >
                Best Testimonials
              </button>
              <button
                className="quick-filter-btn"
                onClick={() => onFilterChange({
                  ...filters,
                  responseStatus: 'pending',
                  sentiment: 'negative'
                })}
              >
                Needs Attention
              </button>
              <button
                className="quick-filter-btn"
                onClick={() => onFilterChange({
                  ...filters,
                  dateRange: 'last7days',
                  hasHighlights: 'yes'
                })}
              >
                Recent Highlights
              </button>
              <button
                className="quick-filter-btn"
                onClick={() => onFilterChange({
                  ...filters,
                  hasHighlights: 'yes',
                  highlightAngle: 'benefit'
                })}
              >
                Benefits Only
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedFilterBar;